import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useI18n } from "../i18n";
import { PARTNERS, CONTACT } from "../lib/contact";
import { useGoogleReviews } from "../lib/useGoogleReviews";

const Counter = ({ to, suffix = "", prefix = "", duration = 1.8 }) => {
  const ref = useRef(null);
  const rafRef = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {val.toLocaleString("pt-PT")}
      {suffix}
    </span>
  );
};

const PartnerLogo = ({ name, logo, scale }) => {
  const [failed, setFailed] = useState(false);
  const showLogo = logo && !failed;
  return (
    <div
      className={`h-20 md:h-24 flex items-center justify-center px-4 rounded-xl transition-all group ${
        showLogo
          ? "bg-white hover:bg-white/95 border border-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.25)]"
          : "bg-[#0A0A0C] border border-white/5 hover:border-white/20"
      }`}
      data-testid={`partner-${name.toLowerCase().replace(/\s+/g, "-")}`}
      title={name}
    >
      {showLogo ? (
        <img
          src={logo}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-10 md:h-12 w-auto max-w-full object-contain"
          style={scale ? { transform: `scale(${scale})`, transformOrigin: "center" } : undefined}
        />
      ) : (
        <span
          className="text-white/55 group-hover:text-white text-sm md:text-base font-medium tracking-tight text-center"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          {name}
        </span>
      )}
    </div>
  );
};

const TrustAuthority = () => {
  const { t, lang } = useI18n();
  const gr = useGoogleReviews();

  const metrics = [
    { value: 1046, prefix: "", suffix: "", label: t.trust.metrics.families },
    { value: 200, prefix: "€", suffix: "M+", label: t.trust.metrics.volume },
    { value: 10, suffix: "", label: t.trust.metrics.banks },
    { value: 2, suffix: "", label: t.trust.metrics.financeiras },
  ];

  return (
    <section
      data-testid="trust-section"
      className="section-light relative py-24 md:py-32 border-y border-[#e7d5de]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="text-xs tracking-[0.25em] uppercase text-[#A60056] font-semibold mb-4">
            {lang === "pt" ? "Autoridade · Confiança" : "Authority · Trust"}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1a0b12] mb-6 text-balance">
            {t.trust.title}
          </h2>
          <p className="text-[#5c4a54] text-base sm:text-lg leading-relaxed">{t.trust.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e7d5de] rounded-2xl overflow-hidden mb-8">
          {metrics.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-8 md:p-10 text-center"
              data-testid={`metric-${i}`}
            >
              <div className="text-4xl md:text-5xl lg:text-6xl font-medium text-[#A60056] tracking-tight mb-2 whitespace-nowrap">
                <Counter to={m.value} suffix={m.suffix} prefix={m.prefix} />
              </div>
              <div className="text-xs sm:text-sm text-[#6b5560] uppercase tracking-wider leading-snug">
                {m.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mb-20">
          <a
            href={CONTACT.gmaps}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#e7d5de] text-[#A60056] text-sm font-semibold hover:border-[#A60056]/50 transition-colors"
            data-testid="google-reviews-link"
          >
            {gr
              ? `★ ${lang === "pt" ? gr.rating.toFixed(1).replace(".", ",") : gr.rating.toFixed(1)} ${lang === "pt" ? "no Google" : "on Google"} · ${gr.total} ${lang === "pt" ? "avaliações" : "reviews"} →`
              : lang === "pt"
                ? "★ Avaliações no Google →"
                : "★ Google reviews →"}
          </a>
        </div>

        <div className="text-center text-xs tracking-[0.25em] uppercase text-[#8a7480] mb-8">
          {t.trust.banksLabel}
        </div>
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4"
          data-testid="partner-logos"
        >
          {PARTNERS.map((p) => (
            <PartnerLogo key={p.name} name={p.name} logo={p.logo} scale={p.scale} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustAuthority;
